import React from "react";
import {useTranslation} from "react-i18next";
import {useTypedSelector} from "helpers/reducers";
import {GroupedCompetitionItem} from "types/entities/GroupedPackageItem";
import {getSelectedCompetition} from "utils/utility";
import {
    usePersonsWithCredits,
    useSelectedCreditsQuantity,
    useSelectedCreditsValue,
} from "utils/hooks/useCompetitionSelection";
import {translatedText} from "pages/Front/Booking/Competitions/competitionUtils";
import CompetitionItemCard from "pages/Front/Booking/Competitions/components/CompetitionItemCard";
import CompetitionOptionItem from "pages/Front/Booking/Competitions/components/CompetitionOptionItem";
import {PackageCategory} from "types/enums/PackageCategory";

interface CompetitionWithOptionsProps {
    competition: GroupedCompetitionItem;
}

const CompetitionWithOptions = (props: CompetitionWithOptionsProps) => {
    const {t} = useTranslation();
    const bookingFilter = useTypedSelector(state => state.bookingState.bookingProcess.filter);
    const selectedCompetitions = useTypedSelector(state => state.bookingState.bookingProcess.competition);
    const personsWithCredits = usePersonsWithCredits();
    const totalSelectedCreditsValue = useSelectedCreditsValue();
    const totalSelectedCreditsQuantity = useSelectedCreditsQuantity();

    const categoriesWithFamily = [
        PackageCategory.Action,
        PackageCategory.Kinder,
    ];

    const isFamilyCategory = bookingFilter.category
        ? categoriesWithFamily.includes(bookingFilter.category)
        : false;

    const options = props.competition.items;
    const isAnyOptionSelected = options.some(option => getSelectedCompetition(selectedCompetitions, option) !== undefined);

    const needsCredit = props.competition.competition?.needs_credit === true;

    const minimumCreditsAmount = Math.min(...options.map(option => option.competition?.needs_credit_amount || 0).filter(count => count > 0), Infinity);
    const minimumPersonsCount = Math.min(...options.map(option => option.minimum_persons_count).filter(count => count > 0), Infinity);

    const isMissingCredits = isFamilyCategory
        ? needsCredit && Number.isFinite(minimumCreditsAmount) && totalSelectedCreditsValue < minimumCreditsAmount
        : needsCredit && Number.isFinite(minimumPersonsCount) && totalSelectedCreditsQuantity < minimumPersonsCount;

    if (options.length === 0) {
        return <></>;
    }

    return (
        <div className={"CompetitionWithOptions w-full"}>
            <CompetitionItemCard
                categoryType={props.competition.competition?.category_type}
                description={translatedText(props.competition.description)}
                descriptionHtml={translatedText(props.competition.description_html)}
                image={props.competition.image}
                infoText={
                    isMissingCredits
                        ? isFamilyCategory
                            ? t("Needs at least {{amount}} credits - currently {{current}}", {
                                  amount: minimumCreditsAmount,
                                  current: totalSelectedCreditsValue,
                              })
                            : t("Needs at least {{amount}} persons with credits - currently {{current}}", {
                                  amount: minimumPersonsCount,
                                  current: totalSelectedCreditsQuantity,
                              })
                        : undefined
                }
                isSelected={isAnyOptionSelected}
                title={translatedText(props.competition.name)}
            >
                {options.map(option => (
                    <CompetitionOptionItem competition={option} groupItems={options} key={option.id} />
                ))}
            </CompetitionItemCard>
        </div>
    );
};

export default CompetitionWithOptions;
